/**
 * Cookie consent queue
 * use callOnAllow('<consentCategoryName>', function() { // code called when consent is given })
 */
class ccmQueue {

    /**
     *
     * @param {{ccmReadyEventName: string, intervalTime: number}} options
     * @constructor
     */
    constructor(options = {}) {
        const config = Object.assign({}, ccmQueue.default, options);
        /**
         *
         * @type {{}}
         * @private
         */
        this._queue = {
            allow: [],
            disallow: [],
        };
        this._ccmReadyEventName = config.ccmReadyEventName;
        this._intervalTime = config.intervalTime;

        this._initEvents();
    }

    /**
     * Get ccm object
     *
     * @return {*}
     */
    get ccm() {
        return window.ccm;
    }

    get _allowQue() {
        return this._queue.allow;
    }

    get _disallowQue() {
        return this._queue.disallow;
    }

    /**
     * Calls the given callback when consent is given
     *
     * @param consentCategoryName
     * @param callBack
     */
    callOnAllow(consentCategoryName, callBack) {
        if (this._isCcmReady()) {
            this._runAllowCallbacks(consentCategoryName, callBack);
        } else {
            this._addAllowQueue(consentCategoryName, callBack);
        }
    }

    /**
     * Calls the given callback when consent is given
     *
     * @param consentCategoryName
     * @param callBack
     */
    callOnDisallow(consentCategoryName, callBack) {
        if (this._isCcmReady()) {
            this._runDisallowCallbacks(consentCategoryName, callBack);
        } else {
            this._addDisallowQueue(consentCategoryName, callBack);
        }
    }

    /**
     * Init ccm ready event
     *
     * @private
     */
    _initEvents() {
        window.addEventListener(this._ccmReadyEventName, this._waitCcmReady.bind(this));
    }

    /**
     * Fix for ccm ready permissions
     *
     * @private
     */
    _waitCcmReady() {
        const interval = setInterval(() => {
            if (!this._isCcmReady()) {
                return;
            }
            clearInterval(interval);
            this._runAllowQueue();
            this._runDisallowQueue();
        }, this._intervalTime);
    }

    /**
     * Call en remove all items from queue
     *
     * @private
     */
    _runAllowQueue() {
        Object.keys(this._allowQue).forEach((consentCategoryName) => {
            this._runAllowCallbacks(consentCategoryName, ...this._allowQue[consentCategoryName]);
            this._allowQue[consentCategoryName] = [];
        });
    }

    /**
     * Call en remove all items from queue
     *
     * @private
     */
    _runDisallowQueue() {
        Object.keys(this._disallowQue).forEach((consentCategoryName) => {
            this._runDisallowCallbacks(consentCategoryName, ...this._disallowQue[consentCategoryName]);
            this._disallowQue[consentCategoryName] = [];
        });
    }

    /**
     * Add callback to the queue
     *
     * @param consentCategoryName
     * @param callBack
     * @private
     */
    _addAllowQueue(consentCategoryName, callBack) {
        if (!this._allowQue[consentCategoryName]) {
            this._allowQue[consentCategoryName] = [];
        }
        this._allowQue[consentCategoryName].push(callBack);
    }

    /**
     * Add callback to the queue
     *
     * @param consentCategoryName
     * @param callBack
     * @private
     */
    _addDisallowQueue(consentCategoryName, callBack) {
        if (!this._disallowQue[consentCategoryName]) {
            this._disallowQue[consentCategoryName] = [];
        }
        this._disallowQue[consentCategoryName].push(callBack);
    }

    /**
     * Call given callbacks if consent is given
     *
     * @param consentCategoryName
     * @param callBacks
     * @private
     */
    _runAllowCallbacks(consentCategoryName, ...callBacks) {
        if (this._isAllowed(consentCategoryName)) {
            callBacks.forEach((callBack) => {
                callBack();
            });
        }
    }
    /**
     * Call given callbacks if consent is given
     *
     * @param consentCategoryName
     * @param callBacks
     * @private
     */
    _runDisallowCallbacks(consentCategoryName, ...callBacks) {
        if (this._isDisallowed(consentCategoryName)) {
            callBacks.forEach((callBack) => {
                callBack();
            });
        }
    }

    /**
     * True if ccm is ready and get_permissions is not empty
     *
     * @return {boolean}
     * @private
     */
    _isCcmReady() {
        return (typeof this.ccm !== 'undefined') && (this.ccm.get_permissions());
    }

    /**
     * Check if consent is given
     *
     * @param consentCategoryName
     * @return {boolean}
     * @private
     */
    _isAllowed(consentCategoryName) {
        return this.ccm.getCategoryConsent(consentCategoryName);
    }

    /**
     * Check if consent is given
     *
     * @param consentCategoryName
     * @return {boolean}
     * @private
     */
    _isDisallowed(consentCategoryName) {
        return !this.ccm.getCategoryConsent(consentCategoryName);
    }
}

/**
 * Default options
 *
 * @type {{ccmReadyEventName: string, intervalTime: number}}
 */
ccmQueue.default = {
    'ccmReadyEventName': 'CCM_done',
    'intervalTime': 1
};

export default ccmQueue;